import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Button, CircularProgress, TextField, Typography } from '@mui/material';
import { useToast } from 'use-toast-mui';
import { uploadImage } from '../../api/image';
import { getCenterAppBanners, updateCenterAppBanners } from '../../api/member-management';
import { errorMessages } from '../../utils/methods';

// 회원앱 홈 배너는 높이 160(AOS dp·iOS pt)·가로 화면폭이라 보통 폰에서 약 2.2:1 로 가운데 기준 잘린다(2026-10-30 두 앱 160 통일)
const MAX_BANNERS = 3;
const BANNER_RATIO = '1080 / 490';
const UPLOAD_DIRECTORY = 'app-banner';

/**
 * 기업회원 상세 '앱 설정' 탭(브랜딩 지점만). 지점에서 받은 배너 사진을 최대 3장 올리고, 장마다 누르면 열 링크를 넣는다.
 * 저장은 3장 통째로 바꾼다. 하나도 없으면 회원앱은 센터 대표 사진을 배너로 쓴다.
 */
export default function CorpAppSettings({ centerId }) {
  const token = useSelector(state => state.user.user.token);
  const toast = useToast();
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  // 조회에 실패한 채로 저장하면 PUT 통째 교체라 기존 배너가 지워진다 → 실패하면 저장을 막는다
  const [loadFailed, setLoadFailed] = useState(false);
  const [uploadingIndex, setUploadingIndex] = useState(null);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);
  const targetIndexRef = useRef(null);

  useEffect(() => {
    if (!centerId) return;
    (async () => {
      try {
        const res = await getCenterAppBanners(token, centerId);
        setBanners((res || []).map(b => ({ imageUrl: b.imageUrl, landingLink: b.landingLink || '' })));
      } catch (error) {
        setLoadFailed(true);
        toast.error(errorMessages(error));
      } finally {
        setLoading(false);
      }
    })();
  }, [centerId]);

  const pickFile = index => {
    targetIndexRef.current = index;
    fileInputRef.current?.click();
  };

  const onFileChange = async e => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const index = targetIndexRef.current;
    if (!file || index === null) return;
    setUploadingIndex(index);
    try {
      const res = await uploadImage(file, token, UPLOAD_DIRECTORY);
      const imageUrl = res?.imageUrl;
      if (!imageUrl) throw new Error('이미지 업로드에 실패했습니다');
      setBanners(prev => {
        const next = [...prev];
        if (index < next.length) next[index] = { ...next[index], imageUrl };
        else next.push({ imageUrl, landingLink: '' });
        return next;
      });
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      setUploadingIndex(null);
    }
  };

  const updateLink = (index, landingLink) =>
    setBanners(prev => prev.map((b, i) => (i === index ? { ...b, landingLink } : b)));

  const remove = index => setBanners(prev => prev.filter((_, i) => i !== index));

  const move = (index, delta) =>
    setBanners(prev => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  const save = async () => {
    setSaving(true);
    try {
      await updateCenterAppBanners(
        token,
        centerId,
        banners.map(b => ({ imageUrl: b.imageUrl, landingLink: b.landingLink.trim() || null })),
      );
      toast.success('앱 배너를 저장했습니다');
    } catch (error) {
      toast.error(errorMessages(error));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  const slots = banners.length < MAX_BANNERS ? [...banners, null] : banners;

  return (
    <Box sx={{ mt: 3 }}>
      <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>회원앱 홈 배너</Typography>
      <Typography sx={{ fontSize: '13px', color: '#666', mt: 1, lineHeight: 1.6 }}>
        지점에서 받은 배너 사진을 최대 {MAX_BANNERS}장 넣습니다. 앱 홈에서 위 순서대로 넘겨 보입니다.
        <br />
        권장 크기 1080×490px(가로:세로 약 2.2:1). 비율이 다르면 가운데를 기준으로 잘립니다.
        <br />
        링크를 넣으면 배너를 눌렀을 때 그 주소가 열리고, 비우면 눌러도 아무 동작이 없습니다. 배너가 없으면 센터 대표
        사진이 보입니다.
      </Typography>

      {loadFailed && (
        <Typography sx={{ fontSize: '13px', color: '#d32f2f', mt: 2 }}>
          저장된 배너를 불러오지 못해 저장을 막았습니다. 화면을 새로고침한 뒤 다시 시도해 주세요.
        </Typography>
      )}

      <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={onFileChange} />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
        {slots.map((banner, index) => (
          <Box
            key={banner ? `${banner.imageUrl}-${index}` : 'empty'}
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              border: '1px solid #e5e5e5',
              borderRadius: '8px',
              p: 2,
            }}>
            <Typography sx={{ width: 24, fontWeight: 700, color: '#4AB3BC' }}>{index + 1}</Typography>
            <Box
              onClick={() => uploadingIndex === null && pickFile(index)}
              sx={{
                width: 324,
                aspectRatio: BANNER_RATIO,
                flexShrink: 0,
                borderRadius: '8px',
                overflow: 'hidden',
                bgcolor: '#f2f2f2',
                border: banner ? 'none' : '1px dashed #bbb',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}>
              {uploadingIndex === index ? (
                <CircularProgress size={24} />
              ) : banner ? (
                <img
                  src={banner.imageUrl}
                  alt={`배너 ${index + 1}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              ) : (
                <Typography sx={{ fontSize: '13px', color: '#888' }}>+ 사진 올리기</Typography>
              )}
            </Box>
            {banner && (
              <>
                <TextField
                  size="small"
                  label="링크(선택)"
                  placeholder="https://"
                  value={banner.landingLink}
                  onChange={e => updateLink(index, e.target.value)}
                  inputProps={{ maxLength: 500 }}
                  sx={{ flex: 1 }}
                />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Button size="small" onClick={() => pickFile(index)} disabled={uploadingIndex !== null}>
                    사진 바꾸기
                  </Button>
                  <Box sx={{ display: 'flex' }}>
                    <Button size="small" onClick={() => move(index, -1)} disabled={index === 0}>
                      ▲
                    </Button>
                    <Button size="small" onClick={() => move(index, 1)} disabled={index === banners.length - 1}>
                      ▼
                    </Button>
                  </Box>
                  <Button size="small" color="error" onClick={() => remove(index)}>
                    삭제
                  </Button>
                </Box>
              </>
            )}
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'end', mt: 3 }}>
        <Button
          variant="contained"
          onClick={save}
          disabled={loadFailed || saving || uploadingIndex !== null}
          sx={{ borderRadius: '5px', minWidth: '183px', height: '40px', fontSize: '13px' }}>
          {saving ? '저장 중…' : '앱 배너 저장'}
        </Button>
      </Box>
    </Box>
  );
}
